import { AppsHelper } from "./appsHelper";
import { setActivePinia } from "pinia";
import { pinia } from "@/stores";
import { useAuthStore, type User } from "@/stores/useAuthStore";
import { useUIStore } from "@/stores/useUIStore";
import { Format } from "./formatHelper";
import type { Customer } from "models";
import router from "@/router";
import type { RouteLocationRaw } from "vue-router";
import { cauth, sessionToken, logout as logoutApi } from "@/services/medkusApi/publiccustomersService";
import { setAuthToken, clearAuthToken } from "@/services/authToken";

function getAuthStore() {
  setActivePinia(pinia);
  return useAuthStore();
}

function getUIStore() {
  setActivePinia(pinia);
  return useUIStore();
}

type StoredCustomer = Customer & {
  preferences?: unknown;
  expiry?: string | Date | null;
  [key: string]: unknown;
};

export default class CustomerHelper {
  static logout(options: { redirect?: boolean } = {}) {
    const redirect = options.redirect ?? true;
    getAuthStore().setUser(null);
    clearAuthToken();
    // Borra la cookie de sesión en el backend (best-effort).
    void logoutApi();
    localStorage.removeItem("user");
    localStorage.removeItem("t");
    localStorage.removeItem("userData");
    if (redirect) {
      router.push("/");
    }
    const jsonData = {
      logout: "true",
    };
    AppsHelper.sendToIOS(window, jsonData);
  }

  static isUserLogged() {
    const user = this.getUser();
    if (!Format.IsNull(user)) {
      return true;
    }
    return false;
  }

  static loginIfNotLogged(
    allowClose: boolean,
    options?: {
      redirect?: string;
      forceModal?: boolean;
    },
  ) {
    const isUserLogged = this.isUserLogged();
    if (!isUserLogged) {
      const currentRoute = router?.currentRoute?.value ?? null;
      const redirectTarget =
        options?.redirect ??
        (currentRoute?.fullPath ? String(currentRoute.fullPath) : "");

      getUIStore().openLogin({
        allowClose,
        goToUrl: redirectTarget ?? "",
      });

      const forceModal = options?.forceModal ?? true;

      if (!forceModal && currentRoute?.name !== "Login") {
        const routeConfig: RouteLocationRaw = redirectTarget
          ? { name: "Login", query: { redirect: redirectTarget } }
          : { name: "Login" };
        router.push(routeConfig).catch(() => null);
      }
    }
    return isUserLogged;
  }

  static async loginWithCredentials(email: string, password: string) {
    const normalizedEmail = typeof email === "string" ? email.trim() : "";
    const normalizedPassword =
      typeof password === "string" ? password.trim() : "";

    if (!normalizedEmail || !normalizedPassword) {
      return {
        success: false as const,
        message: "Email and password are required.",
      };
    }

    try {
      const response = await cauth({
        email: normalizedEmail,
        password: normalizedPassword,
      });

      if (response && (response as { ok?: boolean }).ok) {
        const payload =
          (response as { model?: Record<string, unknown> }).model ?? {};
        const normalizeOptionalString = (value: unknown) => {
          if (value === null || value === undefined) {
            return undefined;
          }
          const text = typeof value === "string" ? value : String(value);
          const trimmed = text.trim();
          return trimmed.length ? trimmed : undefined;
        };
        const normalizeExpiry = (value: unknown): string | Date | null => {
          if (value instanceof Date) {
            return value;
          }
          if (typeof value === "string") {
            return value;
          }
          return null;
        };
        // El token NO se persiste en localStorage; vive en cookie HttpOnly (sesión) y en
        // memoria (para naavi).
        if (typeof payload.token === "string" && payload.token.length) {
          setAuthToken(payload.token);
        }

        const customer: StoredCustomer = {
          id: normalizeOptionalString(payload.id),
          name: payload.name,
          surname: payload.surname,
          email: payload.email,
          phone: payload.phone,
          logo: payload.logo,
          birthDate: payload.birthDate,
          expiry: normalizeExpiry(payload.expiry),
          lang: payload.lang,
          cashReg: payload.cashReg,
          timeZone: payload.timeZone,
          landingUrl: payload.landingUrl,
          customerTypeId: payload.customerTypeId,
        };

        this.updateStorageUser(customer);

        return {
          success: true as const,
          customer,
          response,
        };
      }

      const errorMsg =
        response && typeof response === "object"
          ? (response as { errorMsg?: string }).errorMsg ?? ""
          : "";
      return {
        success: false as const,
        message:
          typeof errorMsg === "string" && errorMsg.length ? errorMsg : null,
        response,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : null;
      return {
        success: false as const,
        message,
        error,
      };
    }
  }

  static getStoragePreferences() {
    const user = this.getUser();
    if (user && !Format.IsNull((user as StoredCustomer).preferences)) {
      return (user as StoredCustomer).preferences;
    }
    return null;
  }

  static getUser(): StoredCustomer | null {
    const localStorageUser = localStorage.getItem("user");
    if (!localStorageUser) {
      return null;
    }

    try {
      const user = JSON.parse(localStorageUser) as StoredCustomer;
      return user;
    } catch {
      return null;
    }
  }

  static parseNumericId(value: unknown): number | null {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      return numeric;
    }
    const parsed =
      typeof value === "string"
        ? Number.parseInt(value, 10)
        : Number.parseInt(String(value), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  static resolveCustomerId(explicitId?: number | string | null): number | null {
    const explicit = this.parseNumericId(explicitId);
    if (explicit !== null) {
      return explicit;
    }
    const user = this.getUser();
    if (!user) {
      return null;
    }
    return this.parseNumericId(user.id);
  }

  static getUserImage() {
    const user = this.getUser();
    return user?.image ?? null;
  }

  static getUserFullName() {
    const user = this.getUser();
    return user?.fullName ?? null;
  }

  static getUserGender() {
    const user = this.getUser();
    return user?.gender ?? null;
  }

  static updateStorageUser(user: StoredCustomer) {
    const sanitized = { ...user } as Record<string, unknown>;
    if ("customerMedkusId" in sanitized) {
      delete sanitized.customerMedkusId;
    }
    localStorage.setItem("user", JSON.stringify(sanitized));
    getAuthStore().setUser(sanitized as User);
  }

  // Al arrancar/recargar: la sesión vive en la cookie HttpOnly. Si hay un usuario guardado,
  // pedimos al backend (cookie) un token fresco y lo dejamos en memoria para naavi. Si la cookie
  // ya no es válida, limpiamos el usuario obsoleto.
  static async ensureValidSession() {
    const user = this.getUser();
    if (!user) {
      return;
    }

    try {
      const res = (await sessionToken()) as { ok?: boolean; token?: string } | null;
      if (res && res.ok && typeof res.token === "string" && res.token.length) {
        setAuthToken(res.token);
        return;
      }
    } catch {
      // ignorado: se trata como sesión no válida abajo
    }

    this.logout({ redirect: false });
  }
}
